import {
  Box,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Link,
} from '@chakra-ui/react';

const ServiceDescriptionPage = () => {
  return (
    <Box
      bg="white"
      color="gray.800"
      maxW="756px"
      mx="auto"
      px={6}
      pt={16}
      pb={24}
      fontFamily="marketingPage"
      fontSize="15px"
      lineHeight="24px">
      <VStack align="start" spacing={16}>
        {/* Header */}
        <Box>
          <Heading as="h1" fontSize="40px" mb={4} fontFamily="marketingPage">
            Service Description – Merkatus.io
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Effective Date: 2025-10-11
          </Text>
          <Text fontSize="sm" color="gray.600">
            Last Updated: 2025-10-11
          </Text>
        </Box>

        {/* Section 1 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            1. Overview
          </Heading>
          <Text>
            Merkatus.io (&quot;the Service&quot;) is a Software-as-a-Service
            (SaaS) platform developed and operated by{' '}
            <strong>Merkatus.io Oy (Merkatus.io)</strong>, a company registered
            in Finland (Business ID: 3541433-4, Address: Lapinlahdenkatu 16,
            00180 Helsinki, Finland). The platform provides advanced marketing
            and sales analytics through{' '}
            <strong>Marketing Mix Modeling (MMM)</strong> and AI-assisted data
            analysis. Merkatus.io enables organizations to understand the
            effectiveness of their marketing and media investments across both
            online and offline channels, supporting data-driven decision-making
            and strategic budget optimization.
          </Text>
        </Box>

        {/* Section 2 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            2. Core Functionality
          </Heading>
          <Text mb={4}>
            Merkatus.io provides customers with access to the following
            features:
          </Text>
          <UnorderedList pl={4} spacing={2}>
            <ListItem>
              <strong>Marketing Mix Modeling (MMM):</strong> Statistical
              modeling to measure and attribute sales or KPI performance to
              different marketing and media activities.
            </ListItem>
            <ListItem>
              <strong>AI-Assisted Insights:</strong> Automated analyses and
              recommendations to support media planning, budget allocation, and
              optimization decisions.
            </ListItem>
            <ListItem>
              <strong>Multi-Channel Data Integration:</strong> Aggregation of
              online and offline marketing data into unified, standardized
              datasets.
            </ListItem>
            <ListItem>
              <strong>Dashboard and Reporting Tools:</strong> Interactive
              dashboards and automated reports (PDF, CSV, and online views) for
              real-time visibility into performance.
            </ListItem>
            <ListItem>
              <strong>Scenario Planning and Optimization:</strong> Tools to
              simulate alternative media and budget scenarios for both short-
              and long-term planning.
            </ListItem>
          </UnorderedList>
          <Text mt={4}>
            Merkatus.io does{' '}
            <strong>not process or require any personal data (PII)</strong> for
            its modeling. All data used within the Service consists of
            aggregated or anonymized datasets, ensuring compliance with the{' '}
            <strong>EU General Data Protection Regulation (GDPR)</strong>.
          </Text>
        </Box>

        {/* Section 3 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            3. Service Availability and Access
          </Heading>
          <Text>
            Merkatus.io is provided as a <strong>cloud-based SaaS</strong>{' '}
            service, accessible through a secure web interface (
            <Link href="https://merkatus.io" color="darkBlue.900" isExternal>
              https://merkatus.io
            </Link>
            ). The Service is available 24/7, excluding scheduled maintenance or
            updates, which will be communicated in advance. User authentication
            and access control are managed through unique credentials provided
            to authorized customer users. Merkatus.io follows best practices in
            cybersecurity and data protection, including encryption in transit
            and at rest.
          </Text>
        </Box>

        {/* Section 4 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            4. Data Sources and Ownership
          </Heading>
          <Text mb={4}>
            Customers retain full ownership of all data they provide to
            Merkatus.io. Merkatus.io only processes customer data as necessary
            to provide the Service, in accordance with the{' '}
            <strong>Data Processing Agreement (DPA)</strong> between the
            parties. Data sources may include:
          </Text>
          <UnorderedList pl={4} spacing={2}>
            <ListItem>Aggregated campaign performance data</ListItem>
            <ListItem>Media spend data (digital and offline)</ListItem>
            <ListItem>
              External market indicators or publicly available datasets
            </ListItem>
            <ListItem>
              Customer-provided business KPIs (non-personal data)
            </ListItem>
          </UnorderedList>
          <Text mt={4}>
            No personal or cookie-based tracking data is used for modeling or
            analysis.
          </Text>
        </Box>

        {/* Section 5 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            5. Service Updates and Support
          </Heading>
          <Text>
            Merkatus.io continuously improves the Merkatus.io platform. Updates
            and feature enhancements are deployed automatically to all customers
            and may include UI improvements, modeling algorithm updates, or
            integration enhancements. Technical and customer support is
            available via{' '}
            <Link
              href="mailto:support@merkatus.io"
              color="darkBlue.900"
              fontWeight="semibold">
              support@merkatus.io
            </Link>
            .
          </Text>
        </Box>

        {/* Section 6 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            6. Service Fees
          </Heading>
          <Text>
            The Service is billed as a <strong>subscription-based model</strong>
            in accordance with the pricing plans published on the Merkatus.io
            website or the Customer’s Order Form. Unless otherwise agreed,
            billing is monthly in advance and subject to the{' '}
            <strong>Terms of Service</strong>.
          </Text>
        </Box>

        {/* Section 7 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            7. Security and Compliance
          </Heading>
          <Text mb={4}>
            Merkatus Oy applies industry-standard security controls, including:
          </Text>
          <UnorderedList pl={4} spacing={2}>
            <ListItem>Encrypted communication (TLS 1.2+)</ListItem>
            <ListItem>Data encryption at rest (AES-256)</ListItem>
            <ListItem>Regular system audits and monitoring</ListItem>
            <ListItem>Strict access controls and logging</ListItem>
          </UnorderedList>
          <Text mt={4}>
            Since Merkatus.io operates solely on non-personal, aggregated data,
            the Service is inherently <strong>GDPR-compliant</strong> and does
            not require end-user consent for modeling purposes.
          </Text>
        </Box>

        {/* Section 8 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            8. Termination and Data Retention
          </Heading>
          <Text>
            Upon termination of the Customer’s subscription, Merkatus.io will
            retain modeling and aggregated data for a maximum of{' '}
            <strong>90 days</strong>, after which all customer-related data will
            be permanently deleted, unless otherwise required by law or mutual
            agreement.
          </Text>
        </Box>

        {/* Section 9 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            9. Contact
          </Heading>
          <Text>
            Merkatus.io Oy
            <br />
            Lapinlahdenkatu 16, 00180 Helsinki, Finland
            <br />
            <Link
              href="mailto:support@merkatus.io"
              color="darkBlue.900"
              fontWeight="semibold">
              support@merkatus.io
            </Link>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default ServiceDescriptionPage;
